import React from 'react';
import {
    Box,
    Button,
    Chip,
    Stack,
    TextField,
    CircularProgress,
    Typography,
} from '@mui/material';
import { AttachFile, Close } from '@mui/icons-material';
import { useCanAccess, useNotify } from 'react-admin';
import {
    validateAttachmentSelection,
    MAX_PHOTOS,
} from './projectFlowUtils';
import { useCreateStepComment } from './useProjectFlow';

interface CommentComposerProps {
    projectId: number;
    stepId: number;
    onSuccess?: () => void;
}

export const CommentComposer: React.FC<CommentComposerProps> = ({
    projectId,
    stepId,
    onSuccess,
}) => {
    const [content, setContent] = React.useState('');
    const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const { canAccess: canCreateComment } = useCanAccess({
        resource: 'project_flow_comment',
        action: 'create',
    });
    const createCommentMutation = useCreateStepComment();
    const notify = useNotify();

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(event.target.files || []);
        if (!files.length) return;

        // Validate selection (size caps apply to all files; mime check skipped so any type is allowed)
        const validation = validateAttachmentSelection([...selectedFiles, ...files]);
        if (!validation.valid) {
            notify(validation.error || 'Invalid file selection', { type: 'warning' });
            return;
        }

        setSelectedFiles(prev => [...prev, ...files]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveFile = (index: number) => {
        setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {
        if (!content.trim() && selectedFiles.length === 0) {
            notify('Please enter a comment or select photos', { type: 'warning' });
            return;
        }

        createCommentMutation.mutate(
            {
                projectId,
                stepId,
                content: content.trim(),
                files: selectedFiles,
            },
            {
                onSuccess: () => {
                    setContent('');
                    setSelectedFiles([]);
                    if (onSuccess) onSuccess();
                },
            }
        );
    };

    if (!canCreateComment) {
        return null;
    }

    const isLoading = createCommentMutation.isPending;
    const hasContent = content.trim().length > 0 || selectedFiles.length > 0;

    return (
        <Stack gap={2} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Add a comment..."
                value={content}
                onChange={(e) => setContent(e.target.value)}
                disabled={isLoading}
                variant="outlined"
                size="small"
            />

            {selectedFiles.length > 0 && (
                <Stack gap={1}>
                    <Typography variant="caption" fontWeight="bold">
                        Attachments ({selectedFiles.length}/{MAX_PHOTOS})
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {selectedFiles.map((file, index) => (
                            <Chip
                                key={index}
                                size="small"
                                variant="outlined"
                                icon={<AttachFile />}
                                label={file.name}
                                onDelete={isLoading ? undefined : () => handleRemoveFile(index)}
                                deleteIcon={<Close />}
                            />
                        ))}
                    </Stack>
                </Stack>
            )}

            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap={1}
            >
                <Box>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        disabled={isLoading || selectedFiles.length >= MAX_PHOTOS}
                        style={{ display: 'none' }}
                    />
                    <Button
                        size="small"
                        startIcon={<AttachFile />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading || selectedFiles.length >= MAX_PHOTOS}
                    >
                        Attach files
                    </Button>
                </Box>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={!hasContent || isLoading}
                >
                    {isLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        'Post Comment'
                    )}
                </Button>
            </Stack>
        </Stack>
    );
};

CommentComposer.displayName = 'CommentComposer';
